'use client';

import { IconCloseCircleFilled } from '@zillow/constellation-icons/react/filled/icon-close-circle-filled';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { ComboboxRootContext } from './context/combobox-root.context';
import { comboboxClearRecipe } from './styles/combobox-clear.styles';
import type { ComboboxClearPropsInterface } from './types/combobox-clear.types';

/**
 * @since 10.0.0
 */
export const ComboboxClear = forwardRef<HTMLSpanElement, ComboboxClearPropsInterface>(
  (props: ComboboxClearPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, onClick, ...rest } = props;
    const { combobox, size } = useContext(ComboboxRootContext);

    const styles = css(comboboxClearRecipe.raw({ size }), cssProp);

    const onClickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        event.stopPropagation();
        combobox.onClickClear(event);
        onClick?.(event);
      },
      [combobox, onClick],
    );

    const content = children || (
      <IconButton
        title="Clear field selection"
        onClick={onClickHandler}
        size="xs"
        shape="circle"
        emphasis="bare"
        tone="brand"
      >
        <Icon>
          <IconCloseCircleFilled />
        </Icon>
      </IconButton>
    );

    return (
      <span
        ref={forwardedRef}
        data-c11n-component="Combobox.Clear"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_combobox__clear', styles, className)}
        {...rest}
      >
        {content}
      </span>
    );
  },
);

ComboboxClear.displayName = 'Combobox.Clear';
